import { useRef, useCallback, useEffect } from 'react';

const NOTIFICATION_SOUND_PATH = '/sounds/notification.mp3';

/**
 * Hook for playing notification sounds
 * - Preloads audio on mount
 * - Listens for service worker messages to play sound
 * - Respects user volume preferences
 */
export function useNotificationSound() {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const volumeRef = useRef(0.5);

  // Initialize audio element
  useEffect(() => {
    audioRef.current = new Audio(NOTIFICATION_SOUND_PATH);
    audioRef.current.preload = 'auto';
    audioRef.current.volume = volumeRef.current;

    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = '';
        audioRef.current = null;
      }
    };
  }, []);

  // Listen for service worker messages to play notification sound
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === 'PLAY_NOTIFICATION_SOUND') {
        playSound();
      }
    };

    navigator.serviceWorker.addEventListener('message', handleMessage);
    return () => {
      navigator.serviceWorker.removeEventListener('message', handleMessage);
    };
  }, []);

  const playSound = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch((error) => {
        // Autoplay may be blocked - this is expected behavior
        console.debug('[NotificationSound] Could not play:', error.message);
      });
    }
  }, []);

  const setVolume = useCallback((volume: number) => {
    const clampedVolume = Math.max(0, Math.min(1, volume));
    volumeRef.current = clampedVolume;
    if (audioRef.current) {
      audioRef.current.volume = clampedVolume;
    }
  }, []);

  const mute = useCallback(() => {
    setVolume(0);
  }, [setVolume]);

  const unmute = useCallback((volume = 0.5) => {
    setVolume(volume);
  }, [setVolume]);

  return {
    playSound,
    setVolume,
    mute,
    unmute,
  };
}
